import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { WorkflowRail } from "@/components/tms/WorkflowRail";
import { DataTable } from "@/components/table/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api/client";
// formatDate unused — timeline uses local formatDateTime
import { CheckCircle2, Circle, MapPin, Truck } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type TrackingEvent = {
  timestamp?: string;
  status?: string;
  location?: string;
  description?: string;
};

type RecentTrackingRow = {
  id: string;
  tracking: string;
  carrier: string;
};

const NO_SELECTION = "__none__";

export default function TrackingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRecentMode = searchParams.get("mode") === "recent";

  const initialTracking = searchParams.get("tracking") ?? "";
  const initialCarrier = searchParams.get("carrier") ?? "UPS";

  const [trackingNumber, setTrackingNumber] = React.useState(initialTracking);
  const [carrier, setCarrier] = React.useState(initialCarrier);
  const [trackingLoading, setTrackingLoading] = React.useState(false);
  const [recentLoading, setRecentLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<TrackingEvent[]>([]);
  const [recentRows, setRecentRows] = React.useState<RecentTrackingRow[]>([]);
  const [carrierOptions, setCarrierOptions] = React.useState<Array<{ label: string; value: string }>>([]);

  const fetchRecent = React.useCallback(async () => {
    setRecentLoading(true);
    setError(null);
    try {
      const response = await apiClient.getShipments({ page: 1, pageSize: 50 });
      const rows = response.items
        .filter((item) => item.trackingNumber)
        .map((item) => ({ id: item.id, tracking: item.trackingNumber ?? "", carrier: item.carrier.code || item.carrier.name }));
      setRecentRows(rows);
    } catch (recentError) {
      setError(recentError instanceof Error ? recentError.message : "Unable to load recent tracking records.");
    } finally {
      setRecentLoading(false);
    }
  }, []);

  const fetchCarriers = React.useCallback(async () => {
    try {
      const carriers = await apiClient.getCarriers();
      const options = carriers
        .map((item) => ({
          label: item.code ? `${item.name} (${item.code})` : item.name,
          value: item.code || item.name,
        }))
        .filter((item) => item.value.trim().length > 0);
      setCarrierOptions(options);
    } catch {
      // no-op; we'll still allow free text fallback via current value.
      setCarrierOptions([]);
    }
  }, []);

  const runTrack = React.useCallback(async (nextTracking: string, nextCarrier: string) => {
    setTrackingLoading(true);
    setError(null);
    try {
      const response = await apiClient.trackShipment(nextTracking.trim(), nextCarrier.trim());
      setEvents(response ?? []);
    } catch (trackError) {
      setError(trackError instanceof Error ? trackError.message : "Unable to load tracking data.");
      setEvents([]);
    } finally {
      setTrackingLoading(false);
    }
  }, []);

  const handleTrack = React.useCallback(() => {
    const nextTracking = trackingNumber.trim();
    const nextCarrier = carrier.trim();

    if (!nextTracking || !nextCarrier) {
      setError("Tracking number and carrier are required.");
      return;
    }

    void runTrack(nextTracking, nextCarrier);
  }, [carrier, runTrack, trackingNumber]);

  React.useEffect(() => {
    void fetchRecent();
    if (!isRecentMode) {
      void fetchCarriers();
    }
  }, [fetchCarriers, fetchRecent, isRecentMode]);

  React.useEffect(() => {
    if (isRecentMode) return;
    if (!initialTracking.trim() || !initialCarrier.trim()) return;
    void runTrack(initialTracking, initialCarrier);
  }, [initialCarrier, initialTracking, isRecentMode, runTrack]);

  const mergedCarrierOptions = React.useMemo(() => {
    const merged = new Map<string, string>();
    for (const option of carrierOptions) {
      merged.set(option.value, option.label);
    }
    for (const row of recentRows) {
      if (!row.carrier.trim()) continue;
      if (!merged.has(row.carrier)) merged.set(row.carrier, row.carrier);
    }
    if (carrier.trim() && !merged.has(carrier.trim())) {
      merged.set(carrier.trim(), carrier.trim());
    }
    return [...merged.entries()].map(([value, label]) => ({ value, label }));
  }, [carrier, carrierOptions, recentRows]);

  if (isRecentMode) {
    return (
      <div className="space-y-6">
        <WorkflowRail current="track" />
        <Card>
          <CardHeader>
            <CardTitle>Recent Tracking</CardTitle>
            <CardDescription>Recent tracked shipments from live bookings.</CardDescription>
          </CardHeader>
          <CardContent>
            {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}
            {recentLoading ? (
              <div className="space-y-3 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-8 flex-1" />
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-28" />
                  </div>
                ))}
              </div>
            ) : null}
            <DataTable
              rows={recentRows}
              getRowId={(row) => row.id}
              columns={[
                { key: "id", label: "Shipment", sortable: true, render: (row) => row.id, getSortValue: (row) => row.id },
                {
                  key: "tracking",
                  label: "Tracking Number",
                  sortable: true,
                  render: (row) => row.tracking,
                  getSortValue: (row) => row.tracking,
                  filter: { type: "text", placeholder: "Tracking" },
                },
                {
                  key: "carrier",
                  label: "Carrier",
                  sortable: true,
                  render: (row) => (
                    <div className="flex items-center gap-2">
                      <CarrierLogo code={row.carrier} name={row.carrier} className="size-8 rounded-md" />
                      <span>{row.carrier}</span>
                    </div>
                  ),
                  getSortValue: (row) => row.carrier,
                  filter: { type: "select" },
                },
                {
                  key: "action",
                  label: "Action",
                  render: (row) => (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/tracking?tracking=${encodeURIComponent(row.tracking)}&carrier=${encodeURIComponent(row.carrier)}`,
                        )
                      }
                    >
                      Open Tracker
                    </Button>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkflowRail current="track" />

      <Card>
        <CardHeader>
          <CardTitle>Track by PRO/Tracking</CardTitle>
          <CardDescription>Use recent shipment data and carrier dropdowns instead of typing raw codes.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Tracking Lookup</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Recent Shipment</Label>
                <Select
                  value={NO_SELECTION}
                  onValueChange={(value) => {
                    if (value === NO_SELECTION) return;
                    const selected = recentRows.find((item) => item.id === value);
                    if (!selected) return;
                    setTrackingNumber(selected.tracking);
                    setCarrier(selected.carrier);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick recent shipment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION}>No selection</SelectItem>
                    {recentRows.map((row) => (
                      <SelectItem key={row.id} value={row.id}>
                        <span className="flex items-center gap-2">
                          <CarrierLogo code={row.carrier} name={row.carrier} className="size-6 rounded" />
                          <span>#{row.id} · {row.tracking} · {row.carrier}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tracking">Tracking Number</Label>
                <Input id="tracking" value={trackingNumber} onChange={(event) => setTrackingNumber(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Carrier</Label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mergedCarrierOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                          <CarrierLogo code={option.value} name={option.label} className="size-6 rounded" />
                          <span>{option.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={trackingLoading} onClick={handleTrack}>
                {trackingLoading ? "Tracking..." : "Track"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/tracking?mode=recent")}>
                Recent Events
              </Button>
            </div>
          </div>
          {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
        </CardContent>
      </Card>

      {events.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Tracking Events</CardTitle>
            <CardDescription>{events.length} events</CardDescription>
          </CardHeader>
          <CardContent>
            <TrackingTimeline events={events} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Timeline helpers                                                   */
/* ------------------------------------------------------------------ */

type StatusCategory = "delivery" | "transit" | "pickup" | "exception" | "other";

/** Determine visual category for a status string */
function statusCategory(status: string | undefined): StatusCategory {
  const s = (status ?? "").trim().toUpperCase();
  if (s.includes("DELIVER")) return "delivery";
  if (s.includes("TRANSIT") || s.includes("IN_TRANSIT") || s.includes("IN TRANSIT")) return "transit";
  if (s.includes("PICKUP") || s.includes("PICKED")) return "pickup";
  if (s.includes("EXCEPTION") || s.includes("DELAY") || s.includes("HOLD")) return "exception";
  return "other";
}

/** Whether a category means "complete / done" */
function isComplete(cat: StatusCategory): boolean {
  return cat === "delivery";
}

/** Badge classes per category */
function statusBadgeClass(cat: StatusCategory): string {
  switch (cat) {
    case "delivery":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "transit":
      return "border-sky-200 bg-sky-50 text-sky-700";
    case "pickup":
      return "border-violet-200 bg-violet-50 text-violet-700";
    case "exception":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "";
  }
}

/** Current-state banner icon — only used for the top-level shipment state */
function currentStateIcon(cat: StatusCategory) {
  if (isComplete(cat)) return <CheckCircle2 className="size-5 text-emerald-600" />;
  if (cat === "transit") return <Truck className="size-5 text-sky-600" />;
  if (cat === "pickup") return <MapPin className="size-5 text-violet-600" />;
  if (cat === "exception") return <Circle className="size-5 text-amber-600" />;
  return <Circle className="size-5 text-muted-foreground" />;
}

/** Current-state banner text color */
function currentStateTextClass(cat: StatusCategory): string {
  if (isComplete(cat)) return "text-emerald-700";
  if (cat === "transit") return "text-sky-700";
  if (cat === "pickup") return "text-violet-700";
  if (cat === "exception") return "text-amber-700";
  return "text-foreground";
}

/** Format a timestamp with time if available */
function formatDateTime(value: string | undefined): { date: string; time: string | null } {
  if (!value) return { date: "-", time: null };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: value, time: null };
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return { date, time };
}

/* ------------------------------------------------------------------ */
/*  Timeline component                                                 */
/* ------------------------------------------------------------------ */

function TrackingTimeline({ events }: { events: TrackingEvent[] }) {
  // Determine current (latest) state from the first event
  const currentEvent = events[0];
  const currentCat = statusCategory(currentEvent?.status);
  const shipmentComplete = isComplete(currentCat);

  // Group events by date
  const grouped = React.useMemo(() => {
    const groups: { dateLabel: string; items: (TrackingEvent & { index: number })[] }[] = [];
    let currentLabel = "";

    for (const [index, event] of events.entries()) {
      const { date } = formatDateTime(event.timestamp);
      if (date !== currentLabel) {
        currentLabel = date;
        groups.push({ dateLabel: date, items: [] });
      }

      const currentGroup = groups[groups.length - 1];
      if (currentGroup) {
        currentGroup.items.push({ ...event, index });
      }
    }

    return groups;
  }, [events]);

  return (
    <div className="space-y-5">
      {/* Current state banner */}
      <div className="flex items-center gap-3">
        {currentStateIcon(currentCat)}
        <div>
          <p className={`text-sm font-semibold ${currentStateTextClass(currentCat)}`}>
            {currentEvent?.description ?? currentEvent?.status ?? "Unknown"}
          </p>
          {currentEvent?.location ? (
            <p className="text-xs text-muted-foreground">{currentEvent.location}</p>
          ) : null}
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* Timeline */}
      <div className="relative">
        {grouped.map((group, gi) => (
          <div key={group.dateLabel} className={gi > 0 ? "mt-5" : ""}>
            {/* Date header */}
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">
              {group.dateLabel}
            </p>

            {/* Events in this date group */}
            <div className="relative ml-[7px]">
              {group.items.map((event, ei) => {
                const isFirstEvent = event.index === 0;
                const isLastEvent = ei === group.items.length - 1 && gi === grouped.length - 1;
                const { time } = formatDateTime(event.timestamp);
                const evtCat = statusCategory(event.status);

                // Dot logic:
                // - If shipment is complete (delivered): tick on first event, plain dot on rest
                // - If not complete: category icon on first (latest) event, plain dot on rest
                let dot: React.ReactNode;
                if (isFirstEvent && shipmentComplete) {
                  dot = <CheckCircle2 className="size-[18px] text-emerald-600" />;
                } else if (isFirstEvent) {
                  dot = currentStateIcon(currentCat);
                } else {
                  dot = (
                    <div className="flex size-[18px] items-center justify-center">
                      <div className="size-2 rounded-full bg-border" />
                    </div>
                  );
                }

                // Line color: emerald if complete, otherwise neutral
                const lineCls = shipmentComplete ? "bg-emerald-200" : "bg-border";

                return (
                  <div key={`${event.timestamp}-${event.index}`} className="relative flex gap-3.5 pb-5 last:pb-0">
                    {/* Vertical line */}
                    {!isLastEvent ? (
                      <div className={`absolute left-[8px] top-[22px] bottom-0 w-px ${lineCls}`} />
                    ) : null}

                    {/* Dot */}
                    <div className="relative z-10 mt-px flex-shrink-0">{dot}</div>

                    {/* Content */}
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-sm ${isFirstEvent ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                          {event.description ?? event.status ?? "Event"}
                        </span>
                        {event.status ? (
                          <Badge variant="outline" className={`text-[10px] ${statusBadgeClass(evtCat)}`}>
                            {event.status}
                          </Badge>
                        ) : null}
                        {time ? (
                          <span className="text-xs text-muted-foreground">{time}</span>
                        ) : null}
                      </div>
                      {event.location ? (
                        <p className="text-xs text-muted-foreground">
                          {event.location}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
